import { WrappedFormUtils } from "ant-design-vue/lib";
import { CommonPaginationResultModel } from "@/models/common-model";
import { GLGlobal, Permission, isGuid, fmtMsg, ServerExceptionCode, MessageHelper, NotificationType } from "commonui";
import { cloneDeep, forEach, includes, isFunction, isNull, isUndefined, uniqBy } from "lodash";
import {
	AccessLevel,
	ConversationStatus,
	CurriculumType,
	PathTypes,
	VcsMenuCategory,
	VcsMenuCategoryPermission
} from "./enum";
import { routemapper } from "./route-mapper";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	AllLanguageSkill,
	CLUValidations,
	CommonRegex,
	ConversationTitle,
	PathIntentDuplicateUtterances,
	PathIntentUtterances
} from "./constants";
import { VCSLevelModel } from "@/models/title-list-model";
import { VersionGroupModel } from "@/services";
import { ContentForEditModel, ContentTextModel } from "@/services/content";
import { v4 } from "uuid";
import { sortBy } from "lodash";
import { CommonLocale } from "@/locales/localeid";
import { uniq } from "lodash";
import { Route } from "vue-router";
import { EmptyRepConversation, IContentConversationRepModel } from "@/models/lesson-plan";
import {
	ConversationIntentChatModel,
	ConversationPathChatModel,
	ConversationPathEntityValueChatModel
} from "@/services/conversation-builder";

type classesType = string | boolean;
export const mergeClasses = (...classes: classesType[]) => {
	return classes.filter((c) => !!c).join(" ");
};

const regexUuid = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;
const conversationOldImagePattern = /^(Conversation|Dialog|Path|Connector|External|Others)\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/;
interface RouteMetaModel {
	requiresAuth: boolean;
	menuId: VcsMenuCategory;
	permissionId: VcsMenuCategoryPermission | null;
	superMenuId: VcsMenuCategory | undefined;
	allowedForAccessLevel?: AccessLevel[];
}

interface AuthorizationReturnModel {
	isAuthorized: boolean;
	navigateTo: string | null | undefined;
}

interface Options {
	addNoneScore?: Function;
	addConversationScore?: Function;
	utteranceUseEntity?: boolean;
}

interface ConversationDialogTrack {
	conversationDialogId: number;
	pathId?: number | null;
	pathName: string | null;
	utterenceId?: number | null;
	utterenceText: string;
	matchingScore?: number;
	isMatched?: boolean;
	attempt: number;
	topIntentName: string;
	matchedIntentName?: string | null;
	matchedIntentId?: number | null;
	matchedEntityId?: number | null;
	matchedEntityName: string | null;
	matchedEntityValue: string | null;
	isHead: boolean;
	isCompleted: boolean;
	conversationDialogName?: string;
}

function checkAccessLevelForRouteMeta(routeMeta: RouteMetaModel, userPermission: Permission) {
	if (
		!routeMeta ||
		!routeMeta.permissionId ||
		routeMeta.permissionId !== userPermission.id ||
		!Array.isArray(routeMeta.allowedForAccessLevel)
	) {
		return true;
	}
	if (userPermission.isVisible) {
		return !!routeMeta.allowedForAccessLevel.find((a) => a === userPermission.accessLevel);
	}
	return true;
}

export function getAutorizationDetails(metaData: RouteMetaModel, permissions: Permission[]): AuthorizationReturnModel {
	if (!metaData.menuId) {
		return { isAuthorized: true, navigateTo: null };
	}

	const currentPermission = permissions?.find(
		(per) => per.id == (!isUndefined(metaData.superMenuId) ? metaData.superMenuId : metaData.menuId)
	);
	if (!isUndefined(currentPermission)) {
		const visibleItems = isUndefined(metaData.superMenuId)
			? currentPermission.items?.filter((per) => per.isVisible && checkAccessLevelForRouteMeta(metaData, per))
			: currentPermission.items?.find((per) => per.isVisible && per.id === metaData.menuId)?.items;

		const permissionItems: Permission[] = [];

		visibleItems?.forEach((element) => {
			if (element.items) {
				element.items
					.filter((ele) => ele.isVisible)
					.forEach((childElement) => {
						permissionItems.push(childElement);
					});
			} else {
				if (element.isVisible) {
					permissionItems.push(element);
				}
			}
		});

		let requiredPermission: Permission | null | undefined;
		let navigateUrl: string | null | undefined = null;
		if (metaData.permissionId) {
			requiredPermission = permissionItems.find((per) => per.id === metaData.permissionId);
		} else {
			if (permissionItems.length > 0) {
				const permissionIds = permissionItems.map((per) => per.id);

				const filteredRecords = routemapper
					.filter((route) => permissionIds.includes(route.MenuCategoryPermissionId))
					.sort((a, b) => a.PriorityInSection - b.PriorityInSection);

				if (filteredRecords.length > 0) {
					requiredPermission = permissionItems.find(
						(per) => per.id === filteredRecords[0].MenuCategoryPermissionId
					);
					navigateUrl = filteredRecords[0].Path;
				} else {
					requiredPermission = permissionItems.sort((a, b) => a.id - b.id)[0];
				}
			} else {
				requiredPermission = null;
			}

			// requiredPermission = permissionItems.length > 0 ? permissionItems.sort((a, b) => a.id - b.id)[0] : null;
			// navigateUrl = routemapper.find((route) => route.MenuCategoryPermissionId === requiredPermission?.id)?.Path;
			// console.log(navigateUrl);
		}

		if (!isUndefined(requiredPermission) && !isNull(requiredPermission)) {
			return { isAuthorized: requiredPermission.isVisible, navigateTo: navigateUrl };
		}
	}

	return { isAuthorized: false, navigateTo: null };
}

export function isLinkVisible(
	menuId: VcsMenuCategory,
	permissionId?: VcsMenuCategoryPermission,
	superMenuId?: VcsMenuCategory
): boolean {
	const permissions = GLGlobal.permissionInfo();

	const metaData = {
		requiresAuth: true,
		menuId: menuId,
		permissionId: isUndefined(permissionId) ? null : permissionId,
		superMenuId: superMenuId
	};
	const authDetails = getAutorizationDetails(metaData, permissions);
	return authDetails.isAuthorized;
}

export function getRouteAccessLevel(route: Route): AccessLevel {
	const permissions = GLGlobal.permissionInfo();
	if (route && route.meta && route.meta.menuId && route.meta.permissionId) {
		const menucategoryPermission = route.meta.permissionId;
		let accessLevel = AccessLevel.None;
		permissions.forEach((f) => {
			if (f.items) {
				f.items.forEach((fi) => {
					if (fi.items) {
						const item = fi.items.find((fiitem) => fiitem.id === menucategoryPermission);
						if (item) {
							accessLevel = item.accessLevel;
						}
					} else if (fi.id === menucategoryPermission) {
						accessLevel = fi.accessLevel;
					}
				});
			} else if (f.id === menucategoryPermission) {
				accessLevel = f.accessLevel;
			}
		});
		return accessLevel;
	}
	return AccessLevel.None;
}

export const disabledDueToAccessLevel = (currentRouteAccessLevel: AccessLevel): boolean => {
	return currentRouteAccessLevel === AccessLevel.Readonly || currentRouteAccessLevel === AccessLevel.ReadonlyExport;
};

export function deepClone(obj) {
	return cloneDeep(obj);
}

export function getEmptyPaginationResponse(): CommonPaginationResultModel {
	return {
		data: [],
		extraData: { activeCnt: 0, allCnt: 0, inActiveCnt: 0, pendingCount: 0 },
		totalCount: 0
	};
}

export async function versionsLoaded(): Promise<boolean> {
	while (!VersionGroupModule.versionGroups.length) {
		await new Promise((res) => setTimeout(() => res(), 500));
	}
	return true;
}

export function isAnyVersionSelected(): boolean {
	if (!isGuid(VersionGroupModule.selectedGsVersion) && !isGuid(VersionGroupModule.selectedLsVersion)) {
		return false;
	}
	return true;
}

export function getSkillSelected(selectedValues: any) {
	const indexOfAll = selectedValues.lastIndexOf(AllLanguageSkill);
	if ((indexOfAll != -1 && indexOfAll === selectedValues.length - 1) || selectedValues.length === 0) {
		return [AllLanguageSkill];
	} else if (indexOfAll != -1 && indexOfAll !== selectedValues.length - 1) {
		const newSelectedValues = selectedValues.filter((v) => v !== AllLanguageSkill);
		return newSelectedValues;
	} else {
		return selectedValues;
	}
}

export function sortLevelByLicenseAndUnit(levels: VCSLevelModel[]) {
	return levels.sort((a, b) => b.curriculumType - a.curriculumType || a.unit - b.unit);
}

export function getEndLevel(levels: VCSLevelModel[], startLevelId: number): VCSLevelModel[] {
	const startLevelObj = levels.find((l) => l.id === startLevelId);
	if (!isUndefined(startLevelObj)) {
		let projectedEndLevel: VCSLevelModel[] = [];

		if (startLevelObj.curriculumType === CurriculumType.LittleSEED) {
			projectedEndLevel = levels.filter(
				(l) =>
					(l.curriculumType === CurriculumType.LittleSEED && l.unit >= startLevelObj.unit) ||
					l.curriculumType === CurriculumType.GrapeSEED
			);
		} else {
			projectedEndLevel = levels.filter(
				(l) => l.curriculumType === startLevelObj.curriculumType && l.unit >= startLevelObj.unit
			);
		}

		// if (projectedEndLevel.length > 1) {
		// 	projectedEndLevel = projectedEndLevel.filter((l) => l.unit !== startLevelObj.unit);
		// }
		return sortLevelByLicenseAndUnit(projectedEndLevel);
	}

	return [];
}

export function getEndLevelOnLicenseType(levels: VCSLevelModel[], startLevelId: number): VCSLevelModel[] {
	const startLevelObj = levels.find((l) => l.id === startLevelId);
	if (!isUndefined(startLevelObj)) {
		let projectedEndLevel: VCSLevelModel[] = [];

		if (startLevelObj.curriculumType === CurriculumType.LittleSEED) {
			projectedEndLevel = levels.filter(
				(l) => l.curriculumType === CurriculumType.LittleSEED && l.unit >= startLevelObj.unit
			);
		} else {
			projectedEndLevel = levels.filter(
				(l) => l.curriculumType === CurriculumType.GrapeSEED && l.unit >= startLevelObj.unit
			);
		}

		return sortLevelByLicenseAndUnit(projectedEndLevel);
	}

	return [];
}

export function replaceMultipleWhiteSpace(str: string) {
	if (str && str !== "") {
		return str.replace(/\s+/g, " ");
	}

	return str;
}

export interface SelectedLevelsModel {
	lsStartUnit: number;
	lsEndUnit: number;
	gsStartUnit: number;
	gsEndUnit: number;
}

export function getSelectedLevels(
	levels: VCSLevelModel[],
	startLevelSelected: number,
	endLevelSelected: number
): SelectedLevelsModel {
	const startUnitInfo = levels.find((l) => l.id === startLevelSelected);
	const endUnitInfo = levels.find((l) => l.id === endLevelSelected);

	let lsStartUnit = 0,
		lsEndUnit = 0,
		gsStartUnit = 0,
		gsEndUnit = 0;

	if (!isUndefined(startUnitInfo) && !isUndefined(endUnitInfo)) {
		if (
			startUnitInfo.curriculumType === CurriculumType.LittleSEED &&
			endUnitInfo.curriculumType === CurriculumType.LittleSEED
		) {
			lsStartUnit = startUnitInfo.unit;
			lsEndUnit = endUnitInfo.unit;
		} else if (
			startUnitInfo.curriculumType === CurriculumType.GrapeSEED &&
			endUnitInfo.curriculumType === CurriculumType.GrapeSEED
		) {
			gsStartUnit = startUnitInfo.unit;
			gsEndUnit = endUnitInfo.unit;
		} else if (
			startUnitInfo.curriculumType === CurriculumType.LittleSEED &&
			endUnitInfo.curriculumType === CurriculumType.GrapeSEED
		) {
			lsStartUnit = startUnitInfo.unit;
			const lsUnits = levels
				.filter((l) => l.curriculumType === CurriculumType.LittleSEED)
				.map(function (o) {
					return o.unit;
				});
			lsEndUnit = Math.max(...lsUnits);
			const gsUnits = levels
				.filter((l) => l.curriculumType === CurriculumType.GrapeSEED)
				.map(function (o) {
					return o.unit;
				});
			gsStartUnit = Math.min(...gsUnits);
			gsEndUnit = endUnitInfo.unit;
		}
	}

	return { lsStartUnit, lsEndUnit, gsStartUnit, gsEndUnit };
}

export function getEndLevelBasedOnStart(levels: VCSLevelModel[], startLevelId: number): VCSLevelModel[] {
	const endlevels: VCSLevelModel[] = [];
	let isEndUnitStart: boolean = false;

	for (let index = 0; index < levels.length; index++) {
		if (levels[index].id == startLevelId) {
			isEndUnitStart = true;
		}

		if (isEndUnitStart) {
			endlevels.push(levels[index]);
		}
	}

	return endlevels;
}

export function fetchLevels(allLevels: VCSLevelModel[], isLSVersion: string, isGSVersion: string): VCSLevelModel[] {
	const units: VCSLevelModel[] = [];

	if (isGuid(isLSVersion)) {
		const lsUnits = allLevels.filter((l) => l.curriculumType == CurriculumType.LittleSEED);
		units.push(...lsUnits);
	}
	if (isGuid(isGSVersion)) {
		const gsUnits = allLevels.filter((l) => l.curriculumType == CurriculumType.GrapeSEED);
		units.push(...gsUnits);
	}

	return units;
}

export function getGridLevels(levels: VCSLevelModel[], startLevelId: number, endLevelId: number): VCSLevelModel[] {
	const gridLevels: VCSLevelModel[] = [];
	let startIndex = 0;
	let endIndex = 0;

	for (let index = 0; index < levels.length; index++) {
		if (levels[index].id == startLevelId) {
			startIndex = index;
		}

		if (levels[index].id == endLevelId) {
			endIndex = index;
		}
	}

	for (let index = startIndex; index <= endIndex; index++) {
		gridLevels.push(levels[index]);
	}

	return gridLevels;
}

//// **************************** UTILS for TitleList ***********************////
export const createTextMap = (
	details: ContentTextModel[]
): { details: ContentTextModel[]; map: Map<string, number> } => {
	const map = new Map<string, number>();
	details = details.map((c) => {
		if (typeof c.id === "number") {
			const id = v4();
			map.set(id, c.id as any);
			c.id = id;
		}
		return c;
	});
	return { details, map };
};

//// **************************** End of UTILS for TitleList ***********************////

// ****************************** util for sorting list having code(string value) ***********************

function findMaxDots(tagListData, key) {
	let maxDots = 0;
	if (tagListData.length) {
		tagListData.forEach((t) => {
			if (t[key]) {
				const dots = (t[key].match(/\./g) || []).length;
				if (dots > maxDots) {
					maxDots = dots;
				}
			}
		});
	}
	return maxDots;
}

export function sortTagList(tagListData, key) {
	const maxDecimalInTagList: number = findMaxDots(tagListData, key);

	let newTagListData = tagListData.map((t) => {
		if (t[key]) {
			const splittedTag = t[key].split(".");
			let newTagObj = { ...t };
			for (let index = 1; index <= maxDecimalInTagList + 1; index++) {
				const value = splittedTag[index - 1] ? parseInt(splittedTag[index - 1]) : 0;
				newTagObj = { ...newTagObj, ["v" + index]: value };
			}
			return newTagObj;
		}
	});

	const sortKeys: string[] = [];
	for (let index = 1; index <= maxDecimalInTagList + 1; index++) {
		sortKeys.push("v" + index);
	}

	newTagListData = sortBy(newTagListData, sortKeys);
	return newTagListData;
}

export function insertSequence(list) {
	let i = 1;
	return list.map((data) => {
		return { ...data, ["sequence"]: i++ };
	});
}

// ****************************** end util for sorting list having code(string value) ****************************

export function spaceValidator(
	form: WrappedFormUtils,
	fieldName: string,
	errorMessage: string,
	rule: any,
	value: string,
	callback: (message?: string) => {}
): void {
	if (value && value.trim() === "") {
		callback(errorMessage);
	}
	callback();
}

export function second2Minute(second: number): string {
	const result: string = (second / 60).toFixed(1);
	return result === "0.0" ? "0" : result;
}

export function minute2Second(time: string): number {
	const times: string[] = time.split(":");
	return parseInt(times[0]) * 60 + parseInt(times[1]);
}

export function pureRichText(richText) {
	return richText.replace(/(<[^>]*>)?/gi, "");
}

export function toSeconds(time: string) {
	const times = time.split(":");
	return parseInt(times[0]) * 60 + parseInt(times[1]);
}

export function stripHtml(html: string) {
	var temporalDivElement = document.createElement("div");
	// Set the HTML content with the provided html string.
	temporalDivElement.innerHTML = html;
	// Retrieve the text property of the element (cross-browser support)
	return temporalDivElement.textContent || temporalDivElement.innerText || "";
}

export function toTimeString(second) {
	const sec = second % 60;
	return `${Math.floor(second / 60)}:${sec > 9 ? sec : `0${sec}`}`;
}

export function getSelectedVersionGroups(
	versionGroups: VersionGroupModel[],
	selectedGSVersion: string,
	selectedLSVersion: string
): VersionGroupModel[] {
	const sortedVersionGroups: VersionGroupModel[] = versionGroups.sort((vga, vgb) => {
		return vga.curriculumType === vgb.curriculumType
			? vga.name.localeCompare(vgb.name)
			: vga.curriculumType - vgb.curriculumType;
	});
	return sortedVersionGroups.filter(
		(vg) =>
			vg.id === VersionGroupModule.selectedGsVersion ||
			vg.id === selectedGSVersion ||
			vg.id === VersionGroupModule.selectedLsVersion ||
			vg.id === selectedLSVersion
	);
}

export function copyToClipboard(text: string) {
	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.style.position = "fixed";
	textarea.style.pointerEvents = "none";
	textarea.style.opacity = "0";
	document.body.appendChild(textarea);

	textarea.select();
	textarea.setSelectionRange(0, text.length);
	document.execCommand("copy");
	textarea.remove();
}
export function readFromClipboard(): Promise<string> {
	// For Chrome/Safari
	if (isFunction(navigator.clipboard.readText)) {
		return navigator.clipboard.readText();
	}
	// For firefox browser
	return new Promise((resolve) => {
		const textarea = document.createElement("textarea");
		textarea.style.position = "fixed";
		textarea.style.pointerEvents = "none";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.focus();
		document.execCommand("paste");
		setTimeout(() => {
			resolve(textarea.value);
			textarea.remove();
		}, 100);
	});
}

export function getUniqueChangedWords(oldTextLines: string[], newTextLines: string[]) {
	// const punctuationRegex = /[.,\/#!$%\^&\*;:?{}=\`~()]/g;

	const allNewWords = newTextLines
		.flatMap((text) => text.trim().replace(/\t/, " ").split(" "))
		.map((text) => text.trim());

	if (!oldTextLines || oldTextLines.length == 0) {
		const allMismatched = uniq(allNewWords);
		return { allMismatched: allMismatched, newAdded: allMismatched };
	} else {
		const allOldWords = oldTextLines
			.flatMap((text) => text.trim().replace(/\t/, " ").split(" "))
			.map((text) => text.trim());

		const oldUniqueWords = uniq(allOldWords).map((word) => {
			return {
				word,
				total: allOldWords.filter((a) => a == word).length
			};
		});

		const newUniqueWords = uniq(allNewWords).map((word) => {
			return {
				word,
				total: allNewWords.filter((a) => a == word).length
			};
		});

		let allMismatchedWords: string[] = [];
		allMismatchedWords = oldUniqueWords
			.filter(
				(old) =>
					newUniqueWords.filter((newWord) => newWord.word == old.word && newWord.total == old.total).length ==
					0
			)
			.map((old) => {
				return old.word;
			});
		const newMismatchedWords = newUniqueWords
			.filter(
				(newWord) =>
					oldUniqueWords.filter((old) => old.word == newWord.word && old.total == newWord.total).length == 0
			)
			.map((old) => {
				return old.word;
			});

		allMismatchedWords = allMismatchedWords.concat(newMismatchedWords);

		// const newAdded = newUniqueWords
		// 	.filter((newWord) => oldUniqueWords.filter((old) => old.word == newWord.word).length == 0)
		// 	.map((old) => {
		// 		return old.word;
		// 	});
		// return { allMismatched: uniq(allMismatchedWords), newAdded: uniq(newMismatchedWords) };
		return { allMismatched: uniq(allMismatchedWords) };
	}
}

export function getDayOfTheWeek() {
	const date = new Date();
	var dayNumber = date.getDay();

	switch (dayNumber) {
		case 0:
			return fmtMsg(CommonLocale.CommonDaySunday);
		case 1:
			return fmtMsg(CommonLocale.CommonDayMonday);
		case 2:
			return fmtMsg(CommonLocale.CommonDayTuesday);
		case 3:
			return fmtMsg(CommonLocale.CommonDayWednesday);
		case 4:
			return fmtMsg(CommonLocale.CommonDayThursday);
		case 5:
			return fmtMsg(CommonLocale.CommonDayFriday);
		case 6:
			return fmtMsg(CommonLocale.CommonDaySaturday);
		default:
			return fmtMsg(CommonLocale.CommonDaySunday);
	}
}

export function base64toBlob(base64Data: string, contentType: string) {
	contentType = contentType || "";
	var sliceSize = 1024;
	var byteCharacters = atob(base64Data);
	var bytesLength = byteCharacters.length;
	var slicesCount = Math.ceil(bytesLength / sliceSize);
	var byteArrays = new Array(slicesCount);

	for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
		var begin = sliceIndex * sliceSize;
		var end = Math.min(begin + sliceSize, bytesLength);

		var bytes = new Array(end - begin);
		for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
			bytes[i] = byteCharacters[offset].charCodeAt(0);
		}
		byteArrays[sliceIndex] = new Uint8Array(bytes);
	}
	return new Blob(byteArrays, { type: contentType });
}

export const includedUuid = (url: string): boolean => {
	const splitUrl = url.split("/");
	const result = regexUuid.test(splitUrl[splitUrl.length - 1].slice(0, 36));
	return result;
};

export const isOldConversationImage = (url: string): boolean => {
	if (!url || url.length === 0) {
		return false;
	}

	const splitUrl = url.split("/");
	if (splitUrl.length < 2) {
		return false;
	}
	const name = splitUrl[splitUrl.length - 2] + "/" + splitUrl[splitUrl.length - 1].slice(0, 36);
	const result = conversationOldImagePattern.test(name);
	return result;
};

export function getActualFileNameAndType(url: string): string {
	const splitUrl = url.split("/");
	let finalURL = url;
	if (isOldConversationImage(url)) {
		//handle the old logic: Image clone instead of reference
		finalURL = splitUrl[splitUrl.length - 1].substr(36);
	}
	return finalURL;
}

export function showErrorMessage({ error_code, error_description }, operation: string) {
	const errorDescriptionPrefix = "Error:";
	const lessonPlanVersions =
		error_description.length > 0 ? error_description[0].replace(errorDescriptionPrefix, "") : "";
	switch (error_code) {
		case ServerExceptionCode.NotContentConversationException:
			MessageHelper.Message(NotificationType.Warning, fmtMsg(CommonLocale.CommonNotContentConversationMessage));
			break;
		case ServerExceptionCode.ContentConversationStrongAssociationException:
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(CommonLocale.CommonContentConversationAssociationMessage, { lessonPlanVersions, operation })
			);
			break;
		case ServerExceptionCode.ArgumentIsNullOrInvalidException:
			MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpArgumentIsNullOrInvalid));
			break;
		default:
			MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpBadRequest));
			break;
	}
}

export function getContentConversationTitle(convName, contentName) {
	return convName + " " + `(${contentName})`;
}

export function getConversationTitle(repConversation: IContentConversationRepModel, isFromSelect: boolean) {
	if (!repConversation || repConversation.contentId === EmptyRepConversation.contentId) {
		return isFromSelect ? EmptyRepConversation.conversationName : ConversationTitle;
	} else {
		return getContentConversationTitle(repConversation.conversationName, repConversation.contentName);
	}
}

export function getRangeArray(start, end) {
	const length = end - start + 1;
	return Array.from({ length }, (_, i) => parseInt(start, 10) + i);
}

export function timeToSeconds(timeSpan: string) {
	if (timeSpan) {
		var a = timeSpan.split(":");
		var seconds = +a[0] * 3600 + +a[1] * 60 + +a[2];
		return seconds;
	}
}

export function getCLUPredictionResponseAsLUISResponse(response) {
	const intents = response.prediction.intents;

	const intent = intents.reduce((prev, current) => {
		return prev.confidenceScore > current.confidenceScore ? prev : current;
	});

	const topIntentObj = {
		score: intent.confidenceScore
	};
	const entities = response.prediction.entities;

	const transformedEntities = entities.reduce((acc, curr) => {
		return {
			...acc,
			[curr.category]: [[curr.text.toLocaleLowerCase()]]
		};
	}, {});

	return { topIntentObj, transformedEntities };
}

export function hasMaxLimitOfUtterancesExceeded(intents, entities, entityValueIds): boolean {
	let allNonPatternUtterancesCount = 0;
	let allPatternUtterencesReplacedWithEntityValuesCount = 0;

	intents.forEach((intent) => {
		const utterances = intent.utterances;
		allNonPatternUtterancesCount += utterances.filter((u) => !u.isPattern).length;
		const patternUtterancesCount = utterances.filter((u) => u.isPattern).length;
		if (patternUtterancesCount) {
			const entityUsed = entities.find((e) => e.id === intent.entityId);
			if (entityUsed) {
				const usedEntityValuesCount = entityUsed.values.filter((v) => entityValueIds.includes(v.id)).length;
				allPatternUtterencesReplacedWithEntityValuesCount += patternUtterancesCount * usedEntityValuesCount;
			}
		}
	});

	return (
		allNonPatternUtterancesCount + allPatternUtterencesReplacedWithEntityValuesCount >
		CLUValidations.allUtterancesMaxCount
	);
}

export function containsAnchorTags(inputString) {
	const anchorTagRegex = /^\<a.*\>.*\<\/a\>/i;
	return anchorTagRegex.test(inputString);
}

export function stripHtmlTags(input: string, textToReplace: string) {
	if (input.match(CommonRegex.textFormattingTagsRegex)) {
		return input.replace(CommonRegex.textFormattingTagsRegex, textToReplace);
	}

	return input;
}

export function stripAnchorTags(inputString: string, textToReplace: string) {
	return inputString.replace(CommonRegex.anchorTagRegex, textToReplace);
}

export function getInnerHtmlFromText(inputString) {
	const tempElement = document.createElement("div");
	tempElement.innerHTML = inputString;
	const extractedText = tempElement.textContent || tempElement.innerText;
	return extractedText;
}

export function convertScoreToString(score: any) {
	return (score * 100).toFixed(2).toString();
}

export function getConversationNextDialogId(
	topIntent: any,
	topIntentObj: any,
	queryText: any,
	entities: any,
	getActiveDialogById: any,
	currentDialogId: any,
	conversationInfo: any,
	getCLUPredictionResponseAsLUISResponse: any,
	getRetryOrFallbackId: any,
	currentResponse: any,
	noneNextDialogId: any,
	getUtteranceValues: any,
	options?: Options
) {
	const { addNoneScore, addConversationScore, utteranceUseEntity } = options as Options;
	const currentDialog = getActiveDialogById(currentDialogId);
	const matchingScore = topIntentObj.score * 100;
	const isMatched = matchingScore >= conversationInfo.intentMatchThreshold;
	const dialogTrack: ConversationDialogTrack = {
		conversationDialogId: currentDialog.id,
		conversationDialogName: currentDialog.name,
		pathId: null,
		pathName: null,
		utterenceId: null,
		utterenceText: queryText,
		matchingScore,
		isMatched,
		attempt: currentDialog.currentRetryCount + 1,
		topIntentName: topIntent,
		matchedIntentName: null,
		matchedIntentId: null,
		matchedEntityId: null,
		matchedEntityName: null,
		matchedEntityValue: null,
		isHead: currentDialog.isHead,
		isCompleted: currentDialog.isConclusion
	};
	if (
		conversationInfo &&
		topIntentObj.score * 100 >= conversationInfo.intentMatchThreshold &&
		!isUndefined(currentDialog) &&
		!isNull(currentDialog)
	) {
		if (currentDialog?.nextDialogId) {
			return [currentDialog?.nextDialogId, { dialogTrack: null }];
		} else {
			const entitiesData = new Map<string, string[]>();
			const entityKeys = Object.keys(entities);
			entityKeys.forEach((e) => {
				if (!entitiesData.has(e)) {
					entitiesData.set(e, entities[e]);
				}
			});

			let currentDialogPaths = currentDialog?.path.filter(
				(p: any) => p.pathTypeId === PathTypes.Normal && p.statusId === ConversationStatus.Active
			);
			let projectedPath: ConversationPathChatModel | undefined = undefined;
			let projectedPathIntent: ConversationIntentChatModel | null = null;
			let projectedEntity: ConversationPathEntityValueChatModel | null = null;

			if (currentDialogPaths.length > 0) {
				currentDialogPaths = currentDialogPaths.sort((a: any, b: any) => a.order - b.order);
				for (var i = 0; i < currentDialogPaths.length; i++) {
					if (!isUndefined(projectedPath)) {
						break;
					}

					const projectedIntent = currentDialogPaths[i].intent.find(
						(i: any) =>
							i.statusId === ConversationStatus.Active &&
							i.name.toLocaleLowerCase() === topIntent.toLocaleLowerCase()
					);

					if (!isUndefined(projectedIntent)) {
						// Entities used by intent.
						const intentEntityIds = uniq(
							projectedIntent.utterence.filter((u: any) => u.entityId).map((u: any) => u.entityId)
						);

						// Relevant pathEntities as per matched intent.
						const pathEntities = currentDialogPaths[i].pathEntityValue.filter((pev: any) =>
							intentEntityIds.includes(pev.entityId)
						);
						const intentUtterancesValues = getUtteranceValues(projectedIntent.utterence, pathEntities);
						const entitiesdataSize = entitiesData.size;
						if (pathEntities.length > 0 && entitiesdataSize > 0) {
							for (var j = 0; j < pathEntities.length; j++) {
								if (entitiesData.has(pathEntities[j].entityName)) {
									const currentEntityValues = entitiesData.get(pathEntities[j].entityName);
									if (
										currentEntityValues?.some((v) =>
											v.includes(pathEntities[j].value.toLocaleLowerCase())
										)
									) {
										if (
											includes(intentUtterancesValues, queryText.toLocaleLowerCase()) &&
											utteranceUseEntity
										) {
											projectedPath = currentDialogPaths[i];
											projectedPathIntent = projectedIntent;
											projectedEntity = pathEntities[j];
											break;
										}
										projectedPath = currentDialogPaths[i];
										projectedPathIntent = projectedIntent;
										break;
									}
								}
							}
						} else {
							projectedPath = currentDialogPaths[i];
							projectedPathIntent = projectedIntent;
							break;
						}
					}
				}
			}

			if (projectedPath) {
				dialogTrack.pathId = projectedPath.id;
				dialogTrack.pathName = projectedPath.value;
			}

			if (projectedPathIntent) {
				dialogTrack.matchedIntentName = projectedPathIntent.name;
				dialogTrack.matchedIntentId = projectedPathIntent.id;
			}

			if (projectedEntity) {
				dialogTrack.matchedEntityId = projectedEntity.entityId;
				dialogTrack.matchedEntityName = projectedEntity.entityName;
				dialogTrack.matchedEntityValue = projectedEntity.value;
			}

			if (!isUndefined(projectedPath) && projectedPath && projectedPath.nextDialogId) {
				if (addConversationScore && convertScoreToString) {
					addConversationScore(queryText, convertScoreToString(topIntentObj.score));
				}
				return [projectedPath.nextDialogId, { dialogTrack, projectedPath }];
			} else {
				if (addNoneScore) {
					addNoneScore(queryText, topIntent, topIntentObj.score);
				}
				currentResponse.prediction.intents = currentResponse.prediction.intents.filter(
					(intent: any) => intent.category != topIntent
				);
				if (currentResponse.prediction.intents.length) {
					const { topIntentObj, transformedEntities } =
						getCLUPredictionResponseAsLUISResponse(currentResponse);
					const topIntent = currentResponse.prediction.intents.reduce((prev: any, current: any) => {
						return prev.confidenceScore > current.confidenceScore ? prev : current;
					});
					return getConversationNextDialogId(
						topIntent.category,
						topIntentObj,
						queryText,
						transformedEntities,
						getActiveDialogById,
						currentDialogId,
						conversationInfo,
						getCLUPredictionResponseAsLUISResponse,
						getRetryOrFallbackId,
						currentResponse,
						noneNextDialogId,
						getUtteranceValues,
						options
					);
				} else {
					return [getRetryOrFallbackId(currentDialog), { dialogTrack }];
				}
			}
		}
	} else if (!isUndefined(currentDialog) && !isNull(currentDialog)) {
		if (addNoneScore) {
			addNoneScore(queryText, topIntent, topIntentObj.score);
		}
		return [getRetryOrFallbackId(currentDialog), { dialogTrack }];
	} else {
		if (addNoneScore) {
			addNoneScore(queryText, topIntent, topIntentObj.score);
		}
		return [noneNextDialogId, null];
	}
}
